import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/start-here", label: "Start Here" },
  { to: "/support-options", label: "Support Options" },
  { to: "/how-i-work", label: "How I Work" },
  { to: "/sleep-help", label: "Sleep Help" },
  { to: "/parent-stories", label: "Parent Stories" },
  { to: "/about", label: "About" },
];

const navClass = ({ isActive }) =>
  `text-sm font-medium tracking-wide transition-colors hover:text-terra ${isActive ? "text-terra" : "text-ink-soft"}`;

export const Wordmark = ({ size = "md" }) => {
  const heights = { sm: "h-9", md: "h-12", lg: "h-24 sm:h-28" };
  return (
    <Link to="/" data-testid="wordmark-link" className="inline-flex group" aria-label="The Nanny Sleep Co. — home">
      <img
        src="/logo-wide.webp"
        alt="The Nanny Sleep Co. — gentle sleep solutions"
        className={`${heights[size]} w-auto transition-[transform,height] duration-500 group-hover:scale-[1.02]`}
      />
    </Link>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-canvas/90 backdrop-blur-md border-b border-ink/[0.07]" data-testid="site-header">
      {/* GRAND BRAND ROW — big logo, centred, shrinks away on scroll */}
      <div
        className={`hidden lg:flex flex-col items-center overflow-hidden transition-[max-height,padding] duration-500 ease-out ${
          scrolled ? "max-h-0 py-0 opacity-0" : "max-h-44 pt-7 pb-5 opacity-100"
        }`}
      >
        <Wordmark size="lg" />
      </div>

      {/* NAV ROW */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between gap-4 transition-[height] duration-500 ${scrolled ? "h-16" : "h-16 lg:h-14"}`}>
          <div className="lg:hidden">
            <Wordmark size={scrolled ? "sm" : "md"} />
          </div>
          {scrolled && (
            <div className="hidden lg:block">
              <Wordmark size="sm" />
            </div>
          )}
          <nav className="hidden lg:flex items-center gap-7 mx-auto" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3 lg:absolute lg:right-6">
            <Link to="/book-a-call" data-testid="nav-book-call" className="hidden xl:inline-flex text-sm font-semibold text-ink hover:text-terra transition-colors">
              Book a free call
            </Link>
            <Link to="/sleep-assessment" data-testid="nav-assessment-cta" className="btn-primary !py-2.5 !px-5 text-sm hidden sm:inline-flex">
              Take the 2-minute assessment
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-lg text-ink hover:bg-warm transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-ink/[0.07] bg-canvas px-4 py-4 space-y-1" aria-label="Mobile" data-testid="mobile-menu">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block px-3 py-3 rounded-xl text-base font-medium ${isActive ? "bg-terra-light text-terra" : "text-ink-soft hover:bg-warm"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="pt-3 flex flex-col gap-2 px-3">
            <Link to="/sleep-assessment" data-testid="mobile-nav-assessment" className="btn-primary w-full">Take the 2-minute assessment</Link>
            <Link to="/book-a-call" data-testid="mobile-nav-book" className="btn-secondary w-full">Book a free 15-minute suitability call</Link>
          </div>
        </nav>
      )}
    </header>
  );
}
